import { useEffect, useRef } from "react";
import { Animated, View } from "react-native";
import styled from "styled-components/native";
import { colors, radius } from "@/lib/theme";

const SkeletonBase = styled(Animated.View)<{
  w?: string;
  h?: number;
  r?: number;
  mb?: number;
}>`
  background-color: ${colors.line};
  border-radius: ${(p) => p.r ?? radius.md}px;
  width: ${(p) => p.w || "100%"};
  height: ${(p) => p.h || 16}px;
  margin-bottom: ${(p) => p.mb ?? 8}px;
  overflow: hidden;
`;

export function Shimmer({
  width,
  height,
  borderRadius,
  marginBottom,
}: {
  width?: string | number;
  height?: number;
  borderRadius?: number;
  marginBottom?: number;
}) {
  const opacity = useRef(new Animated.Value(0.3)).current;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, {
          toValue: 0.7,
          duration: 800,
          useNativeDriver: true,
        }),
        Animated.timing(opacity, {
          toValue: 0.3,
          duration: 800,
          useNativeDriver: true,
        }),
      ]),
    );
    animation.start();
    return () => animation.stop();
  }, []);

  return (
    <SkeletonBase
      style={{ opacity, width: width as any }}
      h={height || 16}
      r={borderRadius}
      mb={marginBottom}
    />
  );
}

// Pre-built skeleton layouts

const SkeletonCard = styled.View`
  margin: 0 20px 8px;
  background-color: ${colors.white};
  border-radius: ${radius.md}px;
  border: 1px solid ${colors.line};
  padding: 14px 16px;
  flex-direction: row;
  align-items: center;
`;

const SkeletonCardLeft = styled.View`
  flex: 1;
`;

export function SlotSkeleton() {
  return (
    <SkeletonCard>
      <SkeletonCardLeft>
        <Shimmer width="60%" height={16} marginBottom={6} />
        <Shimmer width="40%" height={12} marginBottom={0} />
      </SkeletonCardLeft>
      <Shimmer width={80} height={28} borderRadius={999} marginBottom={0} />
    </SkeletonCard>
  );
}

export function SlotListSkeleton({ count = 6 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <SlotSkeleton key={i} />
      ))}
    </>
  );
}

const PostSkeletonCard = styled.View`
  margin: 8px 20px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border: 1px solid ${colors.line};
  padding: 16px;
`;

const PostSkeletonRow = styled.View`
  flex-direction: row;
  align-items: center;
  margin-bottom: 12px;
`;

export function PostSkeleton() {
  return (
    <PostSkeletonCard>
      <PostSkeletonRow>
        <Shimmer width={40} height={40} borderRadius={20} marginBottom={0} />
        <SkeletonCardLeft style={{ marginLeft: 12 }}>
          <Shimmer width="50%" height={14} marginBottom={4} />
          <Shimmer width="30%" height={10} marginBottom={0} />
        </SkeletonCardLeft>
      </PostSkeletonRow>
      <Shimmer width="90%" height={14} marginBottom={6} />
      <Shimmer width="70%" height={14} marginBottom={0} />
    </PostSkeletonCard>
  );
}

export function FeedSkeleton() {
  return (
    <>
      <PostSkeleton />
      <PostSkeleton />
      <PostSkeleton />
    </>
  );
}

const MemberSkeletonRow = styled.View`
  flex-direction: row;
  align-items: center;
  padding: 14px 16px;
  border-bottom-width: 1px;
  border-bottom-color: ${colors.line};
`;

export function MemberListSkeleton({ count = 5 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <MemberSkeletonRow key={i}>
          <Shimmer width={40} height={40} borderRadius={20} marginBottom={0} />
          <SkeletonCardLeft style={{ marginLeft: 12 }}>
            <Shimmer width="60%" height={14} marginBottom={4} />
            <Shimmer width="40%" height={10} marginBottom={0} />
          </SkeletonCardLeft>
        </MemberSkeletonRow>
      ))}
    </>
  );
}

// Match detail skeleton
const MatchDetailWrap = styled.View`
  padding: 20px;
`;
export function MatchDetailSkeleton() {
  return (
    <MatchDetailWrap>
      <Shimmer width={80} height={24} borderRadius={6} marginBottom={12} />
      <Shimmer width="70%" height={22} marginBottom={8} />
      <Shimmer width="50%" height={14} marginBottom={6} />
      <Shimmer width="40%" height={14} marginBottom={24} />
      <Shimmer
        width="100%"
        height={48}
        borderRadius={radius.md}
        marginBottom={24}
      />
      <Shimmer width={120} height={12} marginBottom={12} />
      {Array.from({ length: 5 }).map((_, i) => (
        <MemberSkeletonRow key={i}>
          <Shimmer width={36} height={36} borderRadius={18} marginBottom={0} />
          <SkeletonCardLeft style={{ marginLeft: 10 }}>
            <Shimmer width="50%" height={14} marginBottom={0} />
          </SkeletonCardLeft>
        </MemberSkeletonRow>
      ))}
    </MatchDetailWrap>
  );
}

// Channel list skeleton
const ChannelSkeletonCard = styled.View`
  margin: 0 20px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border: 1px solid ${colors.line};
  overflow: hidden;
`;
export function ChannelListSkeleton() {
  return (
    <>
      <MatchDetailWrap>
        <Shimmer width={120} height={12} marginBottom={0} />
      </MatchDetailWrap>
      <ChannelSkeletonCard>
        {Array.from({ length: 4 }).map((_, i) => (
          <MemberSkeletonRow key={i}>
            <Shimmer
              width={40}
              height={40}
              borderRadius={radius.md}
              marginBottom={0}
            />
            <SkeletonCardLeft style={{ marginLeft: 12 }}>
              <Shimmer width="50%" height={14} marginBottom={4} />
              <Shimmer width="70%" height={10} marginBottom={0} />
            </SkeletonCardLeft>
          </MemberSkeletonRow>
        ))}
      </ChannelSkeletonCard>
    </>
  );
}

// Events skeleton
export function EventListSkeleton() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        <SkeletonCard
          key={i}
          style={{ flexDirection: "column", alignItems: "flex-start" }}
        >
          <Shimmer width="70%" height={16} marginBottom={8} />
          <Shimmer width="50%" height={12} marginBottom={8} />
          <Shimmer width={60} height={20} borderRadius={999} marginBottom={0} />
        </SkeletonCard>
      ))}
    </>
  );
}

// Club screen skeleton
export function ClubSkeleton() {
  return (
    <>
      <MatchDetailWrap style={{ alignItems: "center" }}>
        <Shimmer width={80} height={80} borderRadius={20} marginBottom={16} />
        <Shimmer width="60%" height={20} marginBottom={6} />
        <Shimmer width="30%" height={14} marginBottom={0} />
      </MatchDetailWrap>
      <SkeletonCard
        style={{ flexDirection: "column", alignItems: "flex-start" }}
      >
        <Shimmer width="80%" height={14} marginBottom={6} />
        <Shimmer width="40%" height={22} marginBottom={0} />
      </SkeletonCard>
      <ChannelSkeletonCard style={{ marginTop: 12 }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <MemberSkeletonRow key={i}>
            <Shimmer
              width={36}
              height={36}
              borderRadius={radius.md}
              marginBottom={0}
            />
            <SkeletonCardLeft style={{ marginLeft: 12 }}>
              <Shimmer width="40%" height={14} marginBottom={0} />
            </SkeletonCardLeft>
          </MemberSkeletonRow>
        ))}
      </ChannelSkeletonCard>
    </>
  );
}

// Chat skeleton
export function ChatSkeleton() {
  return (
    <MatchDetailWrap>
      {Array.from({ length: 6 }).map((_, i) => (
        <MemberSkeletonRow
          key={i}
          style={{
            justifyContent: i % 2 === 0 ? "flex-start" : "flex-end",
            borderBottomWidth: 0,
          }}
        >
          {i % 2 === 0 && (
            <Shimmer
              width={32}
              height={32}
              borderRadius={16}
              marginBottom={0}
            />
          )}
          <Shimmer
            width="60%"
            height={44}
            borderRadius={radius.lg}
            marginBottom={0}
          />
        </MemberSkeletonRow>
      ))}
    </MatchDetailWrap>
  );
}

// Net bookings skeleton
export function FinancesSkeleton() {
  return (
    <Animated.View style={{ padding: 20 }}>
      {/* Hero card */}
      <SkeletonBase w="100%" h={120} r={radius.lg} mb={12} />
      {/* Stat cards row */}
      <Animated.View
        style={{ flexDirection: "row", gap: 10, marginBottom: 24 }}
      >
        <SkeletonBase w="50%" h={80} r={radius.lg} mb={0} />
        <SkeletonBase w="50%" h={80} r={radius.lg} mb={0} />
      </Animated.View>
      {/* Section label */}
      <SkeletonBase w="40%" h={12} mb={12} />
      {/* Chart card */}
      <SkeletonBase w="100%" h={180} r={radius.lg} mb={24} />
      {/* Section label */}
      <SkeletonBase w="30%" h={12} mb={12} />
      {/* List rows */}
      <SkeletonBase w="100%" h={64} r={radius.lg} mb={1} />
      <SkeletonBase w="100%" h={64} r={radius.lg} mb={1} />
      <SkeletonBase w="100%" h={64} r={radius.lg} mb={1} />
    </Animated.View>
  );
}

export function MemberDetailSkeleton() {
  return (
    <Animated.View style={{ padding: 20 }}>
      <SkeletonBase w="100%" h={96} r={radius.lg} mb={24} />
      <SkeletonBase w="30%" h={12} mb={12} />
      <SkeletonBase w="100%" h={200} r={radius.lg} mb={24} />
      <SkeletonBase w="40%" h={12} mb={12} />
      <SkeletonBase w="100%" h={64} r={radius.lg} mb={1} />
      <SkeletonBase w="100%" h={64} r={radius.lg} mb={1} />
      <SkeletonBase w="100%" h={64} r={radius.lg} mb={1} />
    </Animated.View>
  );
}

export function NetBookingsSkeleton() {
  return <SlotListSkeleton count={10} />;
}

export function MyFeesSkeleton() {
  return (
    <Animated.View style={{ padding: 20 }}>
      {/* Pay all button */}
      <SkeletonBase w="100%" h={52} r={radius.lg} mb={24} />
      {/* Section label */}
      <SkeletonBase w="30%" h={12} mb={12} />
      {/* Fee rows */}
      {Array.from({ length: 4 }).map((_, i) => (
        <View key={i} style={{ flexDirection: "row", alignItems: "center", marginBottom: 16 }}>
          <Animated.View style={{ flex: 1 }}>
            <SkeletonBase w="60%" h={14} mb={4} />
            <SkeletonBase w="40%" h={10} mb={0} />
          </Animated.View>
          <SkeletonBase w={70} h={32} r={radius.md} mb={0} />
        </View>
      ))}
      {/* Section label */}
      <SkeletonBase w="25%" h={12} mb={12} />
      {/* Paid rows */}
      {Array.from({ length: 3 }).map((_, i) => (
        <View key={i} style={{ flexDirection: "row", alignItems: "center", marginBottom: 16 }}>
          <Animated.View style={{ flex: 1 }}>
            <SkeletonBase w="55%" h={14} mb={4} />
            <SkeletonBase w="35%" h={10} mb={0} />
          </Animated.View>
          <SkeletonBase w={28} h={28} r={14} mb={0} />
        </View>
      ))}
    </Animated.View>
  );
}

const MatchDayCard = styled.View`
  margin: 8px 20px 20px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border: 1px solid ${colors.line};
  padding: 16px;
`;

export function MatchesSkeleton({ count = 4 }: { count?: number }) {
  return (
    <Animated.View style={{ paddingTop: 12 }}>
      {Array.from({ length: count }).map((_, i) => (
        <MatchDayCard key={i}>
          <Shimmer width="40%" height={11} marginBottom={10} />
          <Shimmer width="70%" height={18} marginBottom={6} />
          <Shimmer width="55%" height={12} marginBottom={16} />
          <View style={{ flexDirection: "row", gap: 8 }}>
            <Shimmer
              width="32%"
              height={40}
              borderRadius={radius.md}
              marginBottom={0}
            />
            <Shimmer
              width="32%"
              height={40}
              borderRadius={radius.md}
              marginBottom={0}
            />
            <Shimmer
              width="32%"
              height={40}
              borderRadius={radius.md}
              marginBottom={0}
            />
          </View>
        </MatchDayCard>
      ))}
    </Animated.View>
  );
}
